import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';

import {
  EducationLevelEnum,
  SearchCandidateFilter,
} from 'lib/generated/graphql';

export type UseSearchCandidateFilterResult<
  K extends keyof SearchCandidateFilter
> = [
  value: SearchCandidateFilter[K],
  setValue: Dispatch<SetStateAction<SearchCandidateFilter[K]>>
];

export type DynamicFilterItem = {
  value: string;
  label: string;
};

export type DynamicSkillFilterItem = {
  value: string;
  label: string;
  min: number;
  max: number;
};

export type DynamicLanguageFilterItem = {
  value: string;
  label: string;
  level?: number;
};

export const SALARY_DEFAULT_MIN = 0;
export const SALARY_DEFAULT_MAX = 1000000;

export const defaultInitialValues: SearchCandidateFilter = {
  livingCities: [],
  workCities: [],
  languages: [],
  focus: [],
  careerSlugs: [],
  keywords: '',
  salaryRange: undefined,
  workTypes: undefined,
  workRemote: [],
  skills: [],
  available: true,
  situation: 3,
  orderFilter: 'updated_profile',
  page: 1,
  genders: [],
  specialNecessities: [],
  educationLevel: 'all' as EducationLevelEnum,
};

export const searchCandidateFiltersAtom =
  atom<SearchCandidateFilter>(defaultInitialValues);

export const dynamicSkillFilterItems = atom<DynamicSkillFilterItem[]>([]);
export const dynamicWorkCityFilterItems = atom<DynamicFilterItem[]>([]);
export const dynamicLivingCityFilterItems = atom<DynamicFilterItem[]>([]);
export const dynamicLanguageFilterItems = atom<DynamicLanguageFilterItem[]>([]);

export const availableFocusesFilterItems = atom<DynamicSkillFilterItem[]>([]);

export function useSearchCandidateFilter<K extends keyof SearchCandidateFilter>(
  key: K
): UseSearchCandidateFilterResult<K> {
  const [filterState, setFilterState] = useAtom(searchCandidateFiltersAtom);

  const filterValue = filterState[key];

  const handleOnChange = useCallback(
    (value) => {
      setFilterState((s) => ({
        ...s,
        [key]: value,
        page: 1,
      }));
    },
    [setFilterState, key]
  );

  const payload = useMemo<UseSearchCandidateFilterResult<K>>(
    () => [filterValue, handleOnChange],
    [handleOnChange, filterValue]
  );

  return payload;
}
